import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import googleButton from "../../images/web_light_sq_SI@2x.png";
import logo from "../../images/logo.svg";
import { ensureUserProfile } from "../shared/provisionUser";
import "./auth.css";

const LogInForm = ({ onSwitchToRegister }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] =
    useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const auth = getAuth();
  const navigate = useNavigate();
  const ERROR_MESSAGE = "There is a problem logging in";

  const handleLogIn = (e) => {
    e.preventDefault();

    signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        navigate("/dashboard");
      })
      .catch((error) => {
        setError(error.message);
      });
  };

  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      // A first-time Google user has no users/{uid} profile yet — provision one
      // here (was missing, which left them school-less: no student/attendance
      // access and a broken school-join). New profiles go to onboarding.
      const { created } = await ensureUserProfile(result.user);
      navigate(created ? "/getstarted" : "/dashboard");
    } catch (error) {
      console.error("Error with Google sign-in:", error);
      setError("Google sign-in failed. Please try again.");
    }
  };

  const handleForgotPassword = () => {
    sendPasswordResetEmail(auth, resetEmail)
      .then(() => {
        setResetMessage("Password reset email sent. Please check your inbox.");
        setResetEmail("");
      })
      .catch((error) => {
        setResetMessage(`Error: ${error.message}`);
      });
  };

  const closeForgotPasswordModal = () => {
    setIsForgotPasswordModalOpen(false);
    setResetMessage("");
  };

  // Cross-link to Registration: switch the modal if we're in one, else navigate.
  const goToRegister = () => {
    if (onSwitchToRegister) onSwitchToRegister();
    else navigate("/register");
  };

  return (
    <div className="auth-card">
      <div className="auth-head">
        <img src={logo} className="auth-logo" alt="classBOON" />
        <h1 className="auth-title">Welcome back 👋</h1>
        <p className="auth-sub">Sign in to your classroom.</p>
      </div>

      <form className="auth-form" onSubmit={handleLogIn}>
        <div className="auth-field">
          <label htmlFor="login-email">Email</label>
          <input id="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@school.org" />
        </div>
        <div className="auth-field">
          <label htmlFor="login-pass">Password</label>
          <input id="login-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Your password" />
        </div>
        <button type="submit" className="auth-submit">Log In</button>
        <div className="auth-forgot">
          <button type="button" className="auth-linkbtn" onClick={() => setIsForgotPasswordModalOpen(true)}>
            Forgot password?
          </button>
        </div>
        {error && <div className="auth-error">{ERROR_MESSAGE}</div>}
      </form>

      <div className="auth-divider">or</div>
      <button onClick={handleGoogleSignIn} className="auth-google">
        <img src={googleButton} alt="Sign in with Google" />
      </button>

      <div className="auth-foot">
        New to classBOON?{" "}
        <button type="button" className="auth-linkbtn" onClick={goToRegister}>Create an account</button>
      </div>

      {/* Forgot Password Modal */}
      {isForgotPasswordModalOpen && (
        <div className="modal-overlay" onClick={closeForgotPasswordModal}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Reset your password</h2>
            <p>Enter your email address to receive a password reset link.</p>
            <input
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              placeholder="Email address"
              required
            />
            <div className="modal-buttons">
              <button onClick={handleForgotPassword}>Send Reset Email</button>
              <button onClick={closeForgotPasswordModal} className="btn-secondary">
                Cancel
              </button>
            </div>
            {resetMessage && <p className="auth-reset-msg">{resetMessage}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default LogInForm;
